'use client';

import { usePathname } from 'next/navigation';
import { TeamMatch, TeamMatchHistory } from '../types';
import fetcher, { Response } from '@/lib/utils/fetcherBasic';
import React, { useCallback, useEffect, useState } from 'react';
import { EdfMatches } from '@/lib/directus';
import mapHistoricalDataToTeamMatchHistory from '@/lib/mappers/mapHistoricalDataToTeamMatchHistory';
import DateLabel from '../date/DateLabel';
import TitleH2 from '../common/TitleH2';
import Link from 'next/link';

interface Props {
  data: TeamMatchHistory;
  teamName: string;
  title?: string;
}

type FilterType = 'competition' | 'year' | 'opponent' | 'country' | 'city';

export default function TeamHistory({ data, teamName, title }: Props) {
  const pathname = usePathname();
  const [teamMatchs, setTeamMatchs] = useState<Array<TeamMatch>>(data.matchs);
  const [hash, setHash] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasFilter, setHasFilter] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState<{
    competition: string;
    competitions: Array<string>;
    city: string;
    cities: Array<string>;
    country: string;
    countries: Array<string>;
    opponent: string;
    opponents: Array<string>;
    year: number;
    years: Array<number>;
  }>({
    competition: '',
    competitions: data.competitions,
    city: '',
    cities: data.cities,
    country: '',
    countries: data.countries,
    opponent: '',
    opponents: data.opponents,
    year: -1,
    years: data.years,
  });
  const itemsPerPage = 50;

  const handleSelectCompetitionChanged = (competition: string) => {
    setHasFilter(true);
    setParams((prevState) => {
      return { ...prevState, competition };
    });
  };
  const handleSelectYearChanged = (selectedYear: string) => {
    setHasFilter(true);
    setParams((prevState) => {
      return { ...prevState, year: parseInt(selectedYear) };
    });
  };
  const handleSelectOpponentChanged = (opponent: string) => {
    setHasFilter(true);
    setParams((prevState) => {
      return { ...prevState, opponent };
    });
  };
  const handleSelectCountryChanged = (country: string) => {
    setHasFilter(true);
    setParams((prevState) => {
      return { ...prevState, country };
    });
  };
  const handleSelectCityChanged = (city: string) => {
    setHasFilter(true);
    setParams((prevState) => {
      return { ...prevState, city };
    });
  };

  const clearFilter = (filter: FilterType) => {
    switch (filter) {
      case 'competition': {
        setParams((prevState) => {
          return { ...prevState, competition: '' };
        });
        break;
      }
      case 'year': {
        setParams((prevState) => {
          return { ...prevState, year: -1 };
        });
        break;
      }
      case 'opponent': {
        setParams((prevState) => {
          return { ...prevState, opponent: '' };
        });
        break;
      }
      case 'country': {
        setParams((prevState) => {
          return { ...prevState, country: '' };
        });
        break;
      }
      case 'city': {
        setParams((prevState) => {
          return { ...prevState, city: '' };
        });
        break;
      }
      default:
        break;
    }
  };

  const clearFilters: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    setHasFilter(false);
    setParams((prevState) => {
      return {
        ...prevState,
        year: -1,
        competition: '',
        opponent: '',
        country: '',
        city: '',
      };
    });
  };

  const fetchMatchHistoryData = useCallback(
    async (year: number, opponent: string, competition: string, country: string, city: string) => {
      let queryParams = `?team=${teamName}`;

      if (year > 1900) {
        queryParams += `&year=${year}`;
      }

      if (opponent) {
        queryParams += `&opponent=${opponent}`;
      }

      if (competition) {
        queryParams += `&competition=${competition}`;
      }

      if (country) {
        queryParams += `&country=${country}`;
      }

      if (city) {
        queryParams += `&city=${city}`;
      }

      setLoading(true);
      const response = await fetcher<Array<EdfMatches>>(`/api/match${queryParams}`);

      if ((response as Response).message === 'error') {
        setLoading(false);
      } else {
        const matchs = response as Array<EdfMatches>;

        if (matchs.length > 0) {
          const matchHistory = mapHistoricalDataToTeamMatchHistory(matchs);
          const matches = matchHistory.matchs.reverse().map((match, i) => {
            match.numero = i + 1;
            return match;
          }).reverse();
          setTeamMatchs(matches);
          setParams((prevState) => {
            return {
              ...prevState,
              years: matchHistory.years,
              opponents: matchHistory.opponents,
              countries: matchHistory.countries,
              cities: matchHistory.cities,
              competitions: matchHistory.competitions,
            };
          });
        }
        setLoading(false);
      }
    },
    [teamName],
  );

  const clearFilterComponent = (filter: FilterType, filterValue: string | number) => {
    return (
      <button
        className="btn h-[20px] min-h-[10px] flex-nowrap p-2"
        onClick={() => {
          clearFilter(filter);
        }}
      >
        {filterValue}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    );
  };

  const buildFilterList = () => {
    let clearFilterComponents = [] as React.ReactNode[];
    if (params.competition) {
      clearFilterComponents.push(clearFilterComponent('competition', params.competition));
    }
    if (params.year > 1900) {
      clearFilterComponents.push(clearFilterComponent('year', params.year));
    }
    if (params.opponent) {
      clearFilterComponents.push(clearFilterComponent('opponent', params.opponent));
    }
    if (params.country) {
      clearFilterComponents.push(clearFilterComponent('country', params.country));
    }
    if (params.city) {
      clearFilterComponents.push(clearFilterComponent('city', params.city));
    }

    return (
      <div className="flex gap-2">
        {clearFilterComponents.map((elt, index) => (
          <div key={`filter_${index}`}>{elt}</div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHash(window.location.hash);
    }
  }, []);

  useEffect(() => {
    fetchMatchHistoryData(
      params.year,
      params.opponent,
      params.competition,
      params.country,
      params.city,
    );
  }, [
    fetchMatchHistoryData,
    params.year,
    params.opponent,
    params.competition,
    params.country,
    params.city,
  ]);

  const TableTeamMatchDisplay = () => {
    const paginatedMatchs = teamMatchs.slice(0, itemsPerPage * currentPage);
    return (
      <div>
        <div className="my-7">
          <TitleH2 color="black" size="medium" customFont={true}>
            {title && title}
            {!title && 'Les matchs'}
          </TitleH2>
        </div>

        <div className="relative animate-fadeIn overflow-x-auto">
          <div>
            <div className="relative h-[59px] text-[11px] font-bold uppercase leading-[9.90px] text-slate-950 opacity-100">
              <div className="absolute h-[40px] w-full rounded-md bg-gradient-to-r from-blue-900 via-blue-900 to-red-600 pb-3.5 pl-[83px] pt-4 opacity-20"></div>
              <div className="flex items-center justify-start gap-4 pb-4 pt-4">
                <span className="ml-4 flex w-[70px] items-center justify-start lg:ml-[70px]">
                  Numéro
                </span>
                <span className="w-[200px]">Adversaire</span>
                <span className="w-[150px]">Date</span>
                <span className="w-[200px]">Lieu</span>
                <span className="w-[200px]">Compétition</span>
                <span className="w-[250px]">Résultat</span>
                <span className="w-[50px]"></span>
              </div>
            </div>
            <div className="ml-4 text-sm lg:ml-[70px]">
              {paginatedMatchs.map(
                ({ id: matchId, competition, date, opponent, result, place, numero, score_opponent, score_france, france_at_home }, index) => {
                  let historyUrl = `/${pathname.split('/')[1]}/history-match/${matchId}?matchId=${matchId}&fromMatchId=${matchId}`;
                  if (hash) {
                    historyUrl += hash;
                  }
                  return (
                    <Link
                      key={index}
                      href={historyUrl}
                      scroll={false}
                      aria-label={`Match contre ${opponent} le ${new Date(date).toLocaleDateString()} avec score final ${result}`}
                    >
                      <div
                        key={index}
                        className="flex h-[59px] items-center justify-start gap-4 text-sm text-gray-400 lg:h-[69px]"
                      >
                        <span
                          className={`w-[70px] font-AgencyFBBlackComp text-3xl font-bold tracking-wide text-red-600 antialiased`}
                        >
                          {numero}
                        </span>
                        <span className={`w-[200px] font-bold text-slate-950`}>{opponent}</span>
                        <DateLabel
                          styles="w-[150px]"
                          type="dd/MM/yyyy"
                          isoDate={date}
                          color="gray"
                        />
                        <span className={`w-[200px]`}>{place}</span>
                        <span className={`w-[200px]`}>{competition}</span>
                        <span className={`w-[250px]`}>
                            {france_at_home && (score_france > score_opponent) &&
                                <>
                                  <strong>{`FRANCE`}</strong>
                                  {` - `+opponent+' | '}
                                  <strong>{score_france}</strong>
                                  {' - '+score_opponent}
                                </>
                            }
                            {france_at_home && (score_france < score_opponent) &&
                                <>
                                  {`FRANCE - `}
                                  <strong>{opponent}</strong>
                                  {' | '+score_france+' - '}
                                  <strong>{score_opponent}</strong>
                                </>
                            }
                            {france_at_home && (score_france === score_opponent) &&
                                <>
                                  {`FRANCE - `}
                                  {opponent}
                                  {' | '+score_france+' - '}
                                  {score_opponent}
                                </>
                            }
                            {!france_at_home && (score_france > score_opponent) &&
                                <>
                                  {opponent+' - '}
                                  <strong>{`FRANCE`}</strong>
                                  {' | '+score_opponent+' - '}
                                  <strong>{score_france}</strong>
                                </>
                            }
                            {!france_at_home && (score_france < score_opponent) &&
                                <>
                                  <strong>{opponent}</strong>
                                  {` - FRANCE | `}
                                  <strong>{score_opponent+' - '}</strong>
                                  {score_france}
                                </>
                            }
                            {!france_at_home && (score_france === score_opponent) &&
                                <>
                                  {opponent}
                                  {` - FRANCE | `}
                                  {score_opponent+' - '}
                                  {score_france}
                                </>
                            }
                        </span>
                        <span className={`w-[40px]`}>
                          {(score_france > 0  || score_opponent) ?
                          <>
                          {score_france < score_opponent ?
                            <abbr className="block rounded-full bg-red-600 w-[10px] h-[10px]" title={`Défaite de l'équipe de France`}></abbr>
                            :
                            <abbr className={`block rounded-full ${score_france === score_opponent ? 'bg-gray-600' : 'bg-green-600'} w-[10px] h-[10px]`} title={`${score_france === score_opponent ? 'Match nul' : 'Victoire de l\'équipe de France'}`}></abbr>
                          }</> : ''}
                        </span>
                      </div>
                    </Link>
                  );
                },
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const loadMoreMatches = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const competitions = params.competitions;
  const countries = params.countries;
  const cities = params.cities;
  const opponents = params.opponents;
  const years = params.years;

  const hasDropdownFilters =
    competitions.length > 0 ||
    years.length > 0 ||
    opponents.length > 0 ||
    countries.length > 0 ||
    cities.length > 0;

  return (
    <div className="mb-20 min-h-[800px] py-10 lg:p-10">
      {hasDropdownFilters && (
        <div className="mb-5 flex flex-wrap items-center gap-2.5">
          {competitions.length > 0 && (
            <select
              className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 text-sm font-bold focus:outline-none"
              value={params.competition}
              onChange={(e) => handleSelectCompetitionChanged(e.target.value)}
            >
              <option value="" disabled>
                Compétition
              </option>
              {competitions.map((teamName, index) => {
                return (
                  <option key={index} value={teamName}>
                    {teamName}
                  </option>
                );
              })}
            </select>
          )}
          {years.length > 0 && (
            <select
              className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 text-sm font-bold focus:outline-none"
              value={params.year}
              onChange={(e) => handleSelectYearChanged(e.target.value)}
            >
              <option value="-1" disabled>
                Année
              </option>
              {years.map((teamName, index) => {
                return (
                  <option key={index} value={teamName}>
                    {teamName}
                  </option>
                );
              })}
            </select>
          )}
          {opponents.length > 0 && (
            <select
              className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 text-sm font-bold focus:outline-none"
              value={params.opponent}
              onChange={(e) => handleSelectOpponentChanged(e.target.value)}
            >
              <option value="" disabled>
                Adversaire
              </option>
              {opponents
                .sort((a, b) => a.localeCompare(b))
                .map((opponent, index) => {
                  return (
                    <option key={index} value={opponent}>
                      {opponent}
                    </option>
                  );
                })}
            </select>
          )}
          {countries.length > 0 && (
            <select
              className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 text-sm font-bold focus:outline-none"
              value={params.country}
              onChange={(e) => handleSelectCountryChanged(e.target.value)}
            >
              <option value="" disabled>
                Pays
              </option>
              {countries
                .sort((a, b) => a.localeCompare(b))
                .map((country, index) => {
                  return (
                    <option key={index} value={country}>
                      {country}
                    </option>
                  );
                })}
            </select>
          )}
          {cities.length > 0 && (
            <select
              className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 text-sm font-bold focus:outline-none"
              value={params.city}
              onChange={(e) => handleSelectCityChanged(e.target.value)}
            >
              <option value="" disabled>
                Ville
              </option>
              {cities
                .sort((a, b) => a.localeCompare(b))
                .map((country, index) => {
                  return (
                    <option key={index} value={country}>
                      {country}
                    </option>
                  );
                })}
            </select>
          )}
          <span className="hidden text-sm leading-6 text-gray-400 md:block">
            {teamMatchs.length} matchs trouvés
          </span>
        </div>
      )}
      <div className={`${hasDropdownFilters ? 'my-5' : 'mb-5'} `}>
        {hasFilter && (
          <div className="flex items-center justify-start gap-2">
            <button className="btn h-[20px] min-h-[10px] flex-nowrap p-2" onClick={clearFilters}>
              Supprimer tous les filtres
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            {buildFilterList()}
          </div>
        )}
      </div>
      <div className="min-h-[500px]">
        {loading && (
          <span className="my-4 flex min-h-[300px] items-center justify-center gap-4 text-sm">
            Chargement en cours<span className="loading loading-dots loading-lg"></span>
          </span>
        )}
        {!loading && teamMatchs.length > 0 && TableTeamMatchDisplay()}

        {!loading && teamMatchs.length > 0 && teamMatchs.length > currentPage * itemsPerPage && (
          <div className="flex justify-center hover:animate-bounce">
            <button onClick={loadMoreMatches} className="m-4 gradient-text">
              Voir plus de matchs
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
